/**
 * What an AI test call would say and know, before the phone rings.
 *
 * The test-call dialog opens from the company card, the brain card and the
 * scanner lists. It asks the server for the exact opening, the brain's sales
 * arguments and call script, and whether a real call to this company would be
 * refused, so the owner can read what the AI will do and only then press call.
 *
 * React-free: `aiTestCallPreview.test.ts` runs it under plain node.
 */
import { api } from "./api";
import { brainCompanyPath } from "../crm/sections/prospecting/steps";
import type { AlertClass, AlertFindingId, AlertRecheck, IncidentSource } from "./aiAlert";
import type { OutboundBlocker, OutboundGoal } from "./aiOutbound";

/** How a company is named to the test call routes: by register code, domain, or both. */
export interface AiTestCallTarget {
  regCode?: string | null;
  domain?: string | null;
  /** For the dialog's title while the preview loads. */
  name?: string | null;
  /** The campaign whose words and call flow map the test call uses (ring R8); none from the company card. */
  campaignId?: string | null;
}

/** A campaign's call flow map as the preview reports it: none, followable at a version, or broken. */
export type TestCallFlowPreview =
  | { status: "none" }
  | { status: "ok"; version: number; steps: number }
  | { status: "invalid"; version: number; problems: number };

/**
 * The defect an alert campaign's test call would be about (ring R9). Mirrors
 * `TestCallAlertPreview` (`lib/outbound/test-call-alert.ts`). "none": the
 * company has no defect worth a call right now, and the call is refused. The
 * preview never rechecks; the call does, right before dialling.
 */
export type TestCallAlertPreview =
  | { status: "none" }
  | {
      status: "ready";
      incidentId: string;
      findingId: AlertFindingId;
      class: AlertClass;
      source: IncidentSource;
      firstSeenAt: string;
      confirmedAt: string | null;
      spokenText: string;
      writtenText: string;
      /** The legal-gate address the AI reads out, or null (the AI asks for one). */
      email: string | null;
      recheck: AlertRecheck;
    };

export interface TestCallArgument {
  claim: string;
  impact: string | null;
}

export interface AiOutboundTestCallPreview {
  company: { name: string | null; regCode: string | null; domain: string };
  kind: "warm" | "cold";
  contact: "letter" | "call" | "none";
  goal: OutboundGoal;
  openingText: string;
  openingProblems: string[];
  realCall: { phone: string | null; legalState: string; blockers: string[] };
  knows: {
    arguments: TestCallArgument[];
    analysedDaysAgo: number | null;
    hooks: { text: string; fix: string | null }[];
    questions: string[];
    objections: { cue: string; answer: string }[];
    fitDecision: "go" | "hold" | "no" | null;
    fitReason: string | null;
    touches: { channel: "call" | "email" | "report"; at: string; daysAgo: number }[];
    pitch: string;
  };
  /** What stops the test call right now; empty when it may ring. */
  blockers: OutboundBlocker[];
  /** The campaign's call course; null (or absent from an older server) without a campaign. */
  flow?: TestCallFlowPreview | null;
  /** An alert campaign's defect; null (or absent from an older server) for an offer. */
  alert?: TestCallAlertPreview | null;
}

/** Whether the preview says an alert call has nothing to be about, so the call would be refused. */
export function testCallNoAlert(preview: Pick<AiOutboundTestCallPreview, "alert"> | null): boolean {
  return preview?.alert?.status === "none";
}

/** The request body for a target: empty values left out, a domain lowercased. */
export function testCallBody(target: AiTestCallTarget): { regCode?: string; domain?: string; campaignId?: string } {
  const body: { regCode?: string; domain?: string; campaignId?: string } = {};
  const regCode = target.regCode?.trim();
  const domain = target.domain?.trim().toLowerCase();
  const campaignId = target.campaignId?.trim();
  if (regCode && /^\d{8}$/.test(regCode)) body.regCode = regCode;
  if (domain) body.domain = domain;
  if (campaignId) body.campaignId = campaignId;
  return body;
}

/** Whether a target names anything the server could look up. */
export function hasTestCallTarget(target: AiTestCallTarget | null | undefined): boolean {
  if (!target) return false;
  const body = testCallBody(target);
  return Boolean(body.regCode || body.domain);
}

export function previewAiOutboundTestCall(target: AiTestCallTarget): Promise<AiOutboundTestCallPreview> {
  return api.post("/admin/crm/calls/ai-outbound/test-call/preview", testCallBody(target));
}

/** After this many days the brain's findings are old enough that the AI says so. Mirrors the outbound rules. */
export const STALE_ANALYSIS_DAYS = 14;

/** How much the AI would know: counts per section, and whether it knows nothing at all. */
export function knowsSummary(knows: AiOutboundTestCallPreview["knows"]): {
  arguments: number;
  hooks: number;
  questions: number;
  objections: number;
  empty: boolean;
  stale: boolean;
} {
  const counts = {
    arguments: knows.arguments.length,
    hooks: knows.hooks.length,
    questions: knows.questions.length,
    objections: knows.objections.length,
  };
  return {
    ...counts,
    empty: counts.arguments + counts.hooks + counts.questions + counts.objections === 0,
    stale: knows.analysedDaysAgo !== null && knows.analysedDaysAgo > STALE_ANALYSIS_DAYS,
  };
}

/** The analysis age as a catalogue suffix and its number. */
export function analysisAge(daysAgo: number | null): { key: "unknown" | "today" | "yesterday" | "days"; n: number } {
  if (daysAgo === null || !Number.isFinite(daysAgo)) return { key: "unknown", n: 0 };
  const n = Math.max(0, Math.floor(daysAgo));
  if (n === 0) return { key: "today", n };
  if (n === 1) return { key: "yesterday", n };
  return { key: "days", n };
}

/** The address the brain card of a domain opens at: its company page under
 *  the Arendusaju tab. Spelt once, in the prospecting address table. */
export function brainPath(domain: string): string {
  return brainCompanyPath(domain);
}
